import { AlertColor, TextField } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import { FormDataModel } from '../../../../components/Form';
import axiosInstance from '../../../../utils/axios';
import { PageProps } from '../../../../utils/constants/data';
import { privileges } from '../../../../utils/constants/privileges';
import { routes } from '../../../../utils/constants/routes';
import { URLS } from '../../../../utils/constants/urls';
import { Privilege, languages } from '../../../../utils/redux/reducer/authentication-slice';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import ListPage from '../../CommonPage';
import MasterDataEntityDrawer from '../../CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import { usageTableColumn } from '../../CommonPage/usageColumn';
import { PricePackageForm } from './AddEditPricePackage/PricePackageForm';

const InlineSortOrderCell: FC<{
  row: any;
  canEdit: boolean;
  onSave: (id: number, order: number) => Promise<void>;
}> = ({ row, canEdit, onSave }) => {
  const [value, setValue] = useState(String(row.pricePackageOrder ?? ''));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(String(row.pricePackageOrder ?? ''));
  }, [row.pricePackageOrder, row.id]);

  const commit = async () => {
    const next = Number(value);
    if (!canEdit || saving || Number.isNaN(next) || next === Number(row.pricePackageOrder)) {
      setValue(String(row.pricePackageOrder ?? ''));
      return;
    }
    try {
      setSaving(true);
      await onSave(row.id, next);
    } catch {
      setValue(String(row.pricePackageOrder ?? ''));
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return <>{row.pricePackageOrder ?? '—'}</>;
  }

  return (
    <TextField
      size="small"
      type="number"
      value={value}
      disabled={saving}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      onChange={e => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        }
        if (e.key === 'Escape') {
          setValue(String(row.pricePackageOrder ?? ''));
          (e.target as HTMLInputElement).blur();
        }
      }}
      inputProps={{ min: 0, max: 999999, style: { textAlign: 'center', padding: '6px 8px' } }}
      sx={{ width: 88, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
    />
  );
};

const PricePackageList: FC = () => {
  const navigate = useNavigate();
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const languageList = useSelector(languages) as { text: string; value: string }[];
  const userPrivileges = useSelector(Privilege) as string[];
  const canEdit = userPrivileges?.includes(privileges.edit_price_package);

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const formModel = useMemo(() => {
    const base = [...PricePackageForm(strings)] as any[];
    (languageList || []).forEach(language => {
      const contentField = { ...PricePackageForm(strings)[2] };
      const nameField = { ...PricePackageForm(strings)[3] };
      contentField.field = `name_${language.value}`;
      contentField.label = `${contentField.label} (${language.text})`;
      contentField.responsive = { xs: 12 };
      nameField.field = `description_${language.value}`;
      nameField.label = `${nameField.label} (${language.text})`;
      nameField.responsive = { xs: 12 };
      base.push(nameField, contentField);
    });
    return base
      .filter(item => item.field !== 'description' && item.field !== 'name')
      .map(item => ({ ...item, responsive: { xs: 12, sm: item.field === 'pricePackageOrder' || item.field === 'hsnCode' ? 6 : 12 } }));
  }, [languageList]);

  const getList = useCallback(
    async (filterData: PageProps) => {
      try {
        setLoading(true);
        const body = filterData
          ? filterData
          : { page: index + 1, size: pageSize, filter: filter ?? [], sort: ['pricePackageOrder:asc'] };
        const { data } = await axiosInstance.post(URLS.pricePackage, body);
        setPageResponse(data);
      } catch (error: any) {
        // keep empty on failure
      } finally {
        setLoading(false);
      }
    },
    [index, pageSize, filter],
  );

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const openDrawer = useCallback(
    async (recordId: number | null) => {
      setEditingId(recordId);
      if (recordId) {
        try {
          setDrawerLoading(true);
          const { data } = await axiosInstance.get(URLS.get_edit_delete_pricePackageById(recordId));
          const container: FormDataModel = {
            pricePackageOrder: data.pricePackageOrder ?? '',
            hsnCode: data.hsnCode ?? '',
          };
          (languageList || []).forEach(language => {
            container[`name_${language.value}`] = data?.localizedAttributes?.[language.value]?.description ?? '';
            container[`description_${language.value}`] = data?.localizedAttributes?.[language.value]?.name ?? '';
          });
          setFormValues(container);
        } catch (error: any) {
          handleCatch(error);
          return;
        } finally {
          setDrawerLoading(false);
        }
      } else {
        const container: FormDataModel = {
          pricePackageOrder: '',
          hsnCode: '',
        };
        (languageList || []).forEach(language => {
          container[`name_${language.value}`] = '';
          container[`description_${language.value}`] = '';
        });
        setFormValues(container);
      }
      setDrawerOpen(true);
    },
    [languageList],
  );

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
  };

  const handleSave = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const localizedAttributes = (languageList || []).map(item => ({
        language: item.value,
        name: body[`description_${item.value}`],
        description: body[`name_${item.value}`],
      }));
      const payload = {
        pricePackageOrder: body.pricePackageOrder,
        hsnCode: body.hsnCode,
        localizedAttributes,
      };
      if (editingId) {
        await axiosInstance.put(URLS.get_edit_delete_pricePackageById(editingId), payload);
      } else {
        await axiosInstance.post(URLS.addPricePackage, payload);
      }
      setMessage({
        display: true,
        severity: 'success',
        message: editingId ? strings.common_update_message : strings.common_save_message,
      });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    try {
      setSaving(true);
      await axiosInstance.delete(URLS.get_edit_delete_pricePackageById(editingId));
      setMessage({ display: true, severity: 'success', message: strings.common_delete_message });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSortOrderSave = useCallback(
    async (id: number, pricePackageOrder: number) => {
      try {
        await axiosInstance.patch(URLS.updatePricePackageSortOrder(id), { pricePackageOrder });
        setPageResponse((prev: any) => {
          if (!prev?.content) return prev;
          return {
            ...prev,
            content: prev.content.map((item: any) =>
              item.id === id ? { ...item, pricePackageOrder } : item,
            ),
          };
        });
        setMessage({
          display: true,
          severity: 'success',
          message: strings.common_update_message,
        });
      } catch (error: any) {
        handleCatch(error);
        throw error;
      }
    },
    [],
  );

  const pageConfig = useMemo(
    () => ({
      title: strings.pricePackage_title_list,
      subtitle: strings.pricePackage_list_subtitle,
      searchField: 'pricePackageName',
      searchPlaceholder: 'Search price packages',
      AddButtonText: strings.pricePackage_button_add,
      listApi: URLS.pricePackage,
      deleteApi: URLS.get_edit_delete_pricePackageById,
      addPrivilege: privileges.add_price_package,
      editPrivilege: privileges.edit_price_package,
      deletePrivilege: privileges.delete_price_package,
      customAdd: () => openDrawer(null),
      customEdit: (id: number) => openDrawer(id),
      tableColumn: [
        {
          field: 'pricePackageName',
          headerName: strings.pricePackage_column_name,
          flex: 3,
          sortable: true,
          localField: 'localizedAttributes.name',
        },
        {
          field: 'hsnCode',
          headerName: strings.pricePackage_column_hsn_code,
          flex: 2,
          sortable: false,
        },
        {
          field: 'pricePackageOrder',
          headerName: strings.pricePackage_column_Price_order,
          flex: 1.5,
          sortable: true,
          renderCell: (_value: any, row: any) => (
            <InlineSortOrderCell row={row} canEdit={Boolean(canEdit)} onSave={handleSortOrderSave} />
          ),
        },
        usageTableColumn(row => [
          {
            count: row.venueConfigurationCount,
            singular: 'Config',
            plural: 'Configs',
            onClick: () => navigate(`${routes.venueConfiguration}?pricePackageId=${row.id}`),
          },
          { count: row.bookingCount, singular: 'Booking', plural: 'Bookings' },
        ]),
        lastUpdatedTableColumn(),
      ],
    }),
    [navigate, openDrawer, canEdit, handleSortOrderSave],
  );

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage
        isLoading={loading}
        isWhiteSpace={false}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        getList={getList}
      />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={editingId ? strings.pricePackage_title_edit : strings.pricePackage_title_add}
        isEdit={Boolean(editingId)}
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={userPrivileges?.includes(privileges.delete_price_package)}
        onClose={closeDrawer}
        onSave={handleSave}
        onDelete={handleDelete}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};

export default PricePackageList;
